import { createSelector, createSlice } from '@reduxjs/toolkit';
import { applyPatch, type Operation } from 'fast-json-patch';
import cloneDeep from 'lodash-es/cloneDeep';

import type { Data } from 'ui/data/data';

import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege';
import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import type { ChangesRequestDto } from 'cadenza/api-client/dto/changes-request-dto';
import type { DataProtectionDto } from 'cadenza/api-client/dto/data-protection-dto';
import type { ObjecttypesDataDependenciesDto } from 'cadenza/api-client/dto/objecttype-data-dependencies-dto';
import type { Problem } from 'cadenza/api-client/dto/problem-dto';
import type { ProjectDto } from 'cadenza/api-client/dto/project-dto';
import type { RepositoryDto } from 'cadenza/api-client/dto/repository-dto';
import type { DisplayState, ExecuteCommandResult, GetWorkbookStateResult } from 'cadenza/api-client/workbook-api';
import {
  executeCommand as executeCommandRequest,
  getObjecttypesDataDependencies,
  getPartialWorkbookState,
  getWorkbookState,
  parseWorkbookUrl,
} from 'cadenza/api-client/workbook-api';
import type {
  ChangeCurrentExtentCommandPayload,
  CompositeCommandPayload,
  CopyViewCommandPayload,
  SetDataViewAttributeModificationCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';
import { Command, isCommandType } from 'cadenza/api-client/workbook-commands';
import { hasCapability } from 'cadenza/data-source-util';
import { isFeatureAvailable } from 'cadenza/features';
import type { LoadingState } from 'cadenza/loading-state-cover/loading-state-cover';
import type { DataSourceCapability } from 'cadenza/model/data-source-capability';
import type { WorkbookConfigurationDto } from 'cadenza/model/workbook-configuration';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';
import type { ProjectId, ProjectIdString } from 'cadenza/repository/model/project-id';
import { itemIdToString } from 'cadenza/repository/model/repository-item-id';
import type { WorkbookEmbeddingTargetId } from 'cadenza/repository/model/workbook-embedding-target-id';
import type { CadenzaStore as Store, RootStateOr } from 'cadenza/store/cadenza-store';
import { createAsyncThunk, createSliceFunction, getStore } from 'cadenza/store/cadenza-store';
import { observeStore, STATUS } from 'cadenza/store/state-utils';
import { theme } from 'cadenza/theming-api';
import type { HexColor } from 'cadenza/utils/colors/colors';
import { assert, assertNonNullable } from 'cadenza/utils/custom-error';
import { compareWithLocale } from 'cadenza/utils/i18n/i18n';
import { getLogger } from 'cadenza/utils/logging';
import type { Primitive } from 'cadenza/utils/primitive';
import { cadenzaSessionStorage } from 'cadenza/utils/storage/storage';
import { timeout } from 'cadenza/utils/timeout';
import { entries } from 'cadenza/utils/type-util';
import { getUrlParam } from 'cadenza/utils/url-param-utils';
import type { ReportDto } from 'cadenza/workbook-report/dto/report-dto';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import { AttributeModificationKey } from 'cadenza/workbook/api/attribute-modification-key';
import type { BasicObjecttypeReachabilityDto } from 'cadenza/workbook/api/basic-objecttype-reachability-dto';
import type { DataViewDto } from 'cadenza/workbook/api/data-view-dto';
import type { LayerId } from 'cadenza/workbook/api/layer-id';
import { isObjecttypeBound, type Objecttype } from 'cadenza/workbook/api/objecttype';
import type { ViewDto } from 'cadenza/workbook/api/view-dto';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import type { WorkbookEmbeddingTargetDto } from 'cadenza/workbook/api/workbook-embedding-target-dto';
import { WorkbookId } from 'cadenza/repository/model/workbook-id';
import type { WorkbookNameAndHash } from 'cadenza/repository/model/workbook-name-and-hash';
import type { WorkbookRelation } from 'cadenza/workbook/api/workbook-relation';
import type { WorkbookType } from 'cadenza/workbook/model/workbook-type';
import type { WorkbookVersion } from 'cadenza/workbook/api/workbook-version';
import type { WorksheetDto } from 'cadenza/workbook/api/worksheet-dto';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import {
  areAllHidden,
  conditionHasValues,
  getDeactivated,
  hasValuesSet,
  isDeactivatedForWorksheet,
  isHidden,
  isMandatory,
} from 'cadenza/workbook/conditions/condition-util';
import { UNSET_OBJECTTYPE_ID } from 'cadenza/workbook/data-view/data-view-utils';
import type {
  ViewDrillThroughDto,
  WorksheetDrillThroughDto,
} from 'cadenza/workbook/drill-through/model/drill-through-dto';
import type { FeatureInfoHeadlineType } from 'cadenza/workbook/feature-info-settings-dialog/feature-info-headline-preview/feature-info-headline-preview';
import type { AnalysisContextHierarchyDto } from 'cadenza/workbook/model/analysis-context-hierarchy-dto';
import type { AttributeBundleName } from 'cadenza/workbook/model/attribute-bundle-name';
import { AttributeBundleSettingsKey } from 'cadenza/workbook/model/attribute-bundle-settings-key';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import { getBaseAttributeId, isPartOfObjecttype } from 'cadenza/workbook/model/attribute-id-util';
import { CommonDataViewSettingsKey } from 'cadenza/workbook/model/common-data-view-settings-key';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionGroupDto } from 'cadenza/workbook/model/condition-group-dto';
import type { ConditionGroupId } from 'cadenza/workbook/model/condition-group-id';
import type { ConditionId } from 'cadenza/workbook/model/condition-id';
import { ConditionSettingsKey } from 'cadenza/workbook/model/condition-settings-key';
import type { ConditionToViewsRelationsDto } from 'cadenza/workbook/model/condition-to-views-relations-dto';
import type { DataViewId } from 'cadenza/workbook/model/data-view-id';
import { toDataViewId } from 'cadenza/workbook/model/data-view-id-utils';
import type { HierarchyId } from 'cadenza/workbook/model/hierarchy-id';
import type { HierarchyToViewsRelationsDto } from 'cadenza/workbook/model/hierarchy-to-views-relations-dto';
import { LayerSettingsKey } from 'cadenza/workbook/model/layer-settings-key';
import { MapDataViewSettingsKey } from 'cadenza/workbook/model/map-data-view-settings-key';
import { MapViewSettingsKey } from 'cadenza/workbook/model/map-view-settings-key';
import type { WorkbookConfigDto } from 'cadenza/workbook/model/workbook-config-dto';
import type { WorkbookDataSourceDto } from 'cadenza/workbook/model/workbook-data-source-dto';
import { WorksheetSettingsKey } from 'cadenza/workbook/model/worksheet-settings-key';
import type {
  NotificationSource,
  NotificationType,
  WorkbookNotification,
  WorkbookNotificationId,
} from 'cadenza/workbook/notifications/workbook-notifications';
import { isDataViewNotification, isLayerNotification } from 'cadenza/workbook/notifications/workbook-notifications';
import type { QueryExecutionStrategyType } from 'cadenza/workbook/query-execution-strategy-type';
import type { WorkbookSelectionDto } from 'cadenza/workbook/selection/model/workbook-selection-dto';
import { isAnyDefaultMapDefined } from 'cadenza/workbook/state/default-map.state';
import type { WorkbookLiveModeState } from 'cadenza/workbook/state/workbook-live-mode.state';
import {
  createInitialLiveModeState,
  liveModeReducers,
  stopLiveModeByOther,
} from 'cadenza/workbook/state/workbook-live-mode.state';
import type { MapViewWorkbookState } from 'cadenza/workbook/state/workbook-map-view.state';
import {
  createInitialMapViewState,
  initializeOverviewMapContextsByMainMapViewId,
  isOverviewMap,
  mapViewReducers,
  patchMapViewState,
  patchZoomRelatedViewsState,
} from 'cadenza/workbook/state/workbook-map-view.state';
import {
  createInitialPanelState,
  forcePanelsHidden,
  isPanelVisible,
  type PanelById,
  panelReducers,
  persistPanelState,
  updatePanelStateFromDisplayState,
} from 'cadenza/workbook/state/workbook-panel.state';
import type { WorkbookTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import { addTextViewExtraReducers, createInitialTextViewState } from 'cadenza/workbook/state/workbook-text-view.state';
import { validateViews } from 'cadenza/workbook/view-validation/view-validation';
import type { AttributeDescriptionVisibility } from 'cadenza/workbook/view/view-info/attribute-description-visibility';
import { resolveObjecttypeAttributes } from 'cadenza/workbook/workbook-attribute-util';
import { WORKBOOK_ERROR_KEYS } from 'cadenza/workbook/workbook-error-keys';
import { loadExtentFromSession } from 'cadenza/workbook/workbook-session-storage';
import { type Settings } from 'cadenza/workbook/workbook-settings-util';
import { getDefaultSetting, getSetting, getSettingOrSettingDefault } from 'cadenza/workbook/workbook-settings-util';
import { calculateWorksheetViewBorders } from 'cadenza/workbook/worksheet/worksheet-util';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name';
import type { PublicationStage } from 'cadenza/repository/model/publication-stage';

import type { PayloadAction } from '@reduxjs/toolkit';
import { slugify } from 'cadenza/utils/slugify';

const logger = getLogger('cadenza/workbook');

export const SLICE_PREFIX = 'workbookSlice'; // exported for testing\

declare module 'cadenza/store/cadenza-store' {
  interface RootState {
    [SLICE_PREFIX]: WorkbookState;
  }
}

export const slice = createSliceFunction(SLICE_PREFIX);

const WORKBOOK_CHANGED_KEY = 'workbook-changed';
const PARAMETER_MAXIMIZED_VIEW_ID = 'maximizedViewId';

const parsedWorkbookUrl = parseWorkbookUrl(location.href)!;

/**
 * Enum for the changed state of the workbook.
 */
type ChangedState = 'no' | 'minor' | 'major';

export type ViewMetadata = Record<string, unknown>;

export type ViewWithMetadata = ViewDto & { metadata: ViewMetadata };

export type WorkbookState = WorkbookLiveModeState &
  MapViewWorkbookState &
  WorkbookTextViewState & {
    workbookConfiguration?: WorkbookConfigurationDto;
    workbookName: WorkbookName;
    /** Not present initially, if the hash is not in the URL. */
    workbookNameAndHash?: WorkbookNameAndHash;
    /** The ID of the active (currently shown) worksheet */
    activeWorksheetId: WorksheetId;
    /** The ID of the active view or '' if no view is active */
    activeViewId?: ViewId;
    /** The ID of the maximized view or '' if no view is maximized */
    maximizedViewId: ViewId | '';
    /** workbook repository **/
    repository?: RepositoryDto;
    /** The print name of the workbook */
    printName: string;
    /** The version of the workbook (undefined if the workbook is unsaved) */
    version?: WorkbookVersion;
    publicationStage?: PublicationStage;
    saved: boolean;
    /**
     * Whether the workbook is changed, i.e. whether a command was executed since it was last saved.
     *
     * _Note:_ An unsaved workbook is considered changed.
     */
    changed: ChangedState;
    /**
     * Whether the workbook was changed on the client
     *
     * _Note:_ This is a hack for changes that are not caused by a command execution
     * but should still be persisted on save.
     */
    clientChanged: boolean;
    /** Privileges of the acting user on the workbook */
    privileges: ObjectPrivilege[];
    /** Type of the workbook */
    workbookType: WorkbookType;
    /** Project of the workbook */
    project?: ProjectId;
    /** The workbook's settings */
    settings: Settings;
    /** The workbook config from `workbook-config.xml` */
    config?: WorkbookConfigDto;
    /** The status of the initial workbook state fetching (see `fetchInitialState()`) */
    initializationStatus: STATUS;
    /** The status of the current command execution (see `executeCommand()`) or 'pending' if no command is being executed */
    commandStatus: STATUS;
    /**
     * The error key of the previously executed command error '' if there was no error
     *
     * Used to show specific messages for certain errors.
     *
     * @see problemType in WorkbookExceptionHandler.java
     */
    commandErrorKey: string;
    /** The error detail of the previously executed command error (if any) */
    commandErrorDetail?: unknown;
    /** Projects of the objecttypes that were added to the workbook and are components of the workbook's project plus
     * the project of the workbook */
    projectById: Record<ProjectIdString, ProjectDto>;
    /** Datasources of the objecttypes that were added to the workbook */
    dataSources: WorkbookDataSourceDto[];
    /** Objecttypes that were added to the workbook by ID */
    objecttypeById: Record<ObjecttypeIdString, Objecttype>;
    /** list of unavailable objecttypes that are still added to the workbook (e.g. deleted from repository) */
    unavailableObjecttypeIds: ObjecttypeIdString[];
    /** For each base OT, the other OTs that are reachable, including cardinality of the associations */
    reachableObjecttypesByBasicObjecttypeId: Partial<Record<ObjecttypeIdString, BasicObjecttypeReachabilityDto>>;
    /**
     * Self-Service objecttypes can logically depend on other objecttypes, i.e. if a dependency updates its data,
     * the dependent objecttype should be revised and marked as out of date in a logical order. Mostly relevant
     * for enrichments and copied objecttypes.
     */
    objecttypeDataDependencies: ObjecttypesDataDependenciesDto;
    /** Data protection of the workbook */
    dataProtection?: DataProtectionDto;
    /** Attributes of the objecttypes that were added to the workbook by ID */
    attributeById: Record<AttributeId, WorkbookAttributeDto>;
    /** Attributes that are active (used) in one of the workbook's data views by (active!) ID */
    activeAttributeById: Record<ActiveAttributeId, WorkbookAttributeDto>;
    /** The current dimension/measure value selection in the workbook or undefined if there's no selection */
    selection?: WorkbookSelectionDto;
    /** The IDs of the worksheets of the workbook (in order) */
    worksheets: WorksheetId[];
    /** The worksheets of the workbook by ID */
    worksheetById: Record<WorksheetId, WorksheetDto>;
    /** The views of the workbook by ID */
    viewById: Record<ViewId, ViewDto>;
    /**
     * Metadata of the views of the workbook by ID
     *
     * _Note:_ These client-side metadata are currently separate from the actual views
     * so that they survive view updates from the server.
     */
    viewMetadataById: Record<ViewId, ViewMetadata>;
    /** IDs of views and data views that need to be refreshed */
    invalidViewsOrDataViews: (ViewId | DataViewId)[];
    /** IDs of layers by view id that needs to be refreshed */
    invalidLayerIdsByViewId: Record<ViewId, LayerId[]>;
    /** IDs of data views that have an outdated selection state */
    dataViewsAffectedBySelectionChange: DataViewId[];
    /** The data views of the workbook by ID */
    dataViewById: Record<DataViewId, DataViewDto>;
    /** The current notifications for data views, by view ID */
    notificationsByView: Record<ViewId, Record<WorkbookNotificationId, WorkbookNotification>>;
    /** The curated reports of the workbook By ID */
    reportById: Record<ReportId, ReportDto>;
    /** The embedding targets of the workbook by ID */
    embeddingTargetById: Record<WorkbookEmbeddingTargetId, WorkbookEmbeddingTargetDto>;
    /**
     * Workbook specific drill-through definitions
     * (not including the legacy drill-throughs defined via Cadenza Desktop)
     */
    drillThroughTargets: (WorksheetDrillThroughDto | ViewDrillThroughDto)[];
    /** List of condition IDs and condition group IDs that defines the order within the global analysis context */
    conditionsAndGroups: (ConditionId | ConditionGroupId)[];
    /** All conditions of the workbook by ID (global and locals ones) */
    conditionById: Record<ConditionId, ConditionDto>;
    /** Views and data views affected by a condition by condition ID */
    affectedViewsByConditionId: Record<ConditionId, ConditionToViewsRelationsDto>;
    /** Views and data views affected by a hierarchy by condition ID */
    affectedViewsByHierarchyId: Record<HierarchyId, HierarchyToViewsRelationsDto>;
    /** Conditions groups of the workbook by ID (Always global, we don't have local condition groups.) */
    conditionGroupById: Record<ConditionGroupId, ConditionGroupDto>;
    /** Hierarchies that are controlled in the analysis context by ID */
    hierarchyById: Record<HierarchyId, AnalysisContextHierarchyDto>;
    /**
     * The workbook panel states by panel ID
     *
     * Panels are visible and not disabled by default.
     */
    panelById: PanelById;
    /** true if the workbook state is used from within a PDF report */
    reportMode: boolean;
    forceMaximizePreviousState?: { maximizedViewId: ViewId | ''; panelById: PanelById };
    displayState?: DisplayState;
    relation?: WorkbookRelation;
    isOnDemandWorksheetLoading: boolean;
    /** ID of the layer which settings should be displayed in the designer, will be reset after first display */
    activeLayerId?: LayerId;

    /* Need to be in store, because we need to have separate one for store and secondaryStore */
    lastCommand?: Command;
  };

// Note: If you add properties here which are not coming from the backend, please take a look at the
// fetchInitialState reducer in order not to overwrite properties.
// Note: It's not a static object, but function because initial state depends on what we have in
// session storage. When we use static object then in fetchInitialState we override the state wrongly
function createInitialState(): WorkbookState {
  const workbookName = parsedWorkbookUrl?.workbookName ?? ('' as WorkbookName);
  return {
    workbookConfiguration: getWorkbookConfigFromPageData(),
    workbookName, // fallback for testing
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    workbookNameAndHash: parsedWorkbookUrl?.workbookNameAndHash ?? ({} as WorkbookNameAndHash), // fallback for testing
    activeWorksheetId: parsedWorkbookUrl?.worksheetId ?? ('' as WorksheetId), // fallback for testing
    maximizedViewId: getInitialMaximizedViewIdFromUrlParam(PARAMETER_MAXIMIZED_VIEW_ID),
    printName: '',
    saved: false,
    changed: 'major',
    clientChanged: cadenzaSessionStorage.getItem(WORKBOOK_CHANGED_KEY) === workbookName,
    privileges: [],
    workbookType: 'WORKBOOK',
    settings: {},
    initializationStatus: STATUS.PENDING,
    commandStatus: STATUS.PENDING,
    commandErrorKey: '',
    projectById: {},
    dataSources: [],
    objecttypeById: {},
    reachableObjecttypesByBasicObjecttypeId: {},
    unavailableObjecttypeIds: [],
    objecttypeDataDependencies: { dependencyStateByObjecttypeId: {} },
    attributeById: {},
    activeAttributeById: {},
    worksheets: [],
    worksheetById: {},
    viewById: {},
    viewMetadataById: {},
    invalidViewsOrDataViews: [],
    invalidLayerIdsByViewId: {},
    dataViewsAffectedBySelectionChange: [],
    dataViewById: {},
    notificationsByView: {},
    reportById: {},
    embeddingTargetById: {},
    drillThroughTargets: [],
    conditionsAndGroups: [],
    conditionById: {},
    affectedViewsByConditionId: {},
    affectedViewsByHierarchyId: {},
    conditionGroupById: {},
    hierarchyById: {},
    reportMode: false,
    isOnDemandWorksheetLoading: false,
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    repository: {} as RepositoryDto, // for testing
    ...createInitialPanelState(),
    ...createInitialLiveModeState(),
    ...createInitialMapViewState(),
    ...createInitialTextViewState(),
  };
}

export const reloadObjecttype = async (objecttypeId: ObjecttypeIdString) => {
  const fetchInitial = getStore().dispatch(fetchInitialState());
  // invalidate dataViews explicitly as map dataViews wouldn't get refreshed due to special caching logic
  const invalidateDataViews = getStore().dispatch(invalidateDataViewsUsingObjecttype(objecttypeId));
  return Promise.allSettled([fetchInitial, invalidateDataViews]);
};

/**
 * Creates an async thunk for fetching the initial workbook state.
 *
 * @return A command result (reusing the DTO from the command execution)
 */
export const fetchInitialState = createAsyncThunk(
  SLICE_PREFIX + '/fetchInitialState',
  // do not catch the error here: it would result in an infinite loading spinner
  // as the caller relies on the "rejected" state of redux as this is executed async.
  (
    arg:
      | {
          workbookNameAndHash?: WorkbookNameAndHash;
          secondaryStoreMerge?: boolean;
        }
      | undefined,
    thunkAPI,
  ) => {
    const data = document.querySelector<Data<GetWorkbookStateResult>>('#initial-workbook-state');
    if (data) {
      data.remove();
      return Promise.resolve(data.value);
    }
    return getWorkbookState(arg?.workbookNameAndHash ?? getWorkbookNameAndHash(thunkAPI.getState()));
  },
);

export const resetState = createAsyncThunk(SLICE_PREFIX + '/resetState', () => {});

/**
 * Creates an async thunk for fetching a partial workbook state. Only items requested in the provided changesRequestDto will be requested from backend and updated in the workbook state.
 * Note that current implementation does not invalidate any view/dataView.
 * In case you need this for your use case, ensure that the implementation in the backed supports it.
 */
export const fetchPartialState = createAsyncThunk(
  SLICE_PREFIX + '/fetchPartialState',
  (changesRequestDto: Partial<ChangesRequestDto>, thunkAPI) => {
    return getPartialWorkbookState(getWorkbookNameAndHash(thunkAPI.getState()), {
      objecttypeIds: changesRequestDto.objecttypeIds ?? [],
      updateDataProtection: changesRequestDto.updateDataProtection ?? false,
    });
  },
);

export const fetchDataDependencyState = createAsyncThunk(
  SLICE_PREFIX + '/objecttypeDataDependencyState',
  (arg, thunkAPI) => getObjecttypesDataDependencies(getWorkbookNameAndHash(thunkAPI.getState())),
);

let currentCommandPromise: Promise<void> | undefined;

/**
 * Creates an async thunk for executing a command.
 *
 * Only one command is executed at a time.
 *
 * @param command - The command to be executed
 * @return The command result
 */
export const executeCommand = createAsyncThunk(SLICE_PREFIX + '/executeCommand', (command: Command, thunkAPI) => {
  logger.log('Schedule command', command);

  /*
   * Promise.prototype.finally() won't work, because the return value of the final callback
   * is ignored, i.e. Promise.resolve(1).finally(() => 2) will be resolved with 1.
   * See https://developer.mozilla.org/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally
   */
  const promise = Promise.allSettled([currentCommandPromise])
    .then(() => {
      logger.info('Execute command', command);
      return executeCommandRequest(getWorkbookNameAndHash(thunkAPI.getState()), command);
    })
    .catch((error) => {
      logger.error('Failed to execute command', command, error);
      // In order to pass the error to the `rejected` callback, we need to use this Redux method
      // instead of just throwing the error here.
      return thunkAPI.rejectWithValue(error);
    })
    .finally(() => {
      currentCommandPromise = undefined;
    }) as Promise<ExecuteCommandResult>;

  // currentCommandPromise is used to block the next commands from executing until the first
  // command is fulfilled (including store update). Since redux will write the data into store
  // after we return from this method (payloadCreator), we need to ensure that we fulfill the
  // currentCommandPromise afterward. That's why we need an additional timeout() here.
  currentCommandPromise = promise.then((result) => {
    logger.log('Command executed', command, result);
    return timeout();
  });

  return promise;
});

function setStateFromUpdate(
  state: WorkbookState,
  action: PayloadAction<
    GetWorkbookStateResult,
    string,
    {
      arg: unknown;
    }
  >,
) {
  const {
    operations,
    invalidViewsOrDataViews,
    invalidLayerIdsByViewId,
    dataViewsAffectedBySelectionChange,
    version,
    publicationStage,
  } = action.payload;
  Object.assign(state, updateWorkbookState(state, { patch: operations }), { version, publicationStage });
  updatePanelStateFromDisplayState(state);
  state.clientChanged = false;

  state.unavailableObjecttypeIds = action.payload.unavailableObjecttypeIds ?? [];
  updateDataViewInvalidationState(
    state,
    invalidViewsOrDataViews,
    invalidLayerIdsByViewId,
    dataViewsAffectedBySelectionChange,
  );
}

function resetToEmpty(state: RootStateOr<WorkbookState>) {
  Object.assign(state, createInitialState());
}

function doAddNotifications(state: RootStateOr<WorkbookState>, viewId: ViewId, added: WorkbookNotification[]) {
  const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
  added.forEach((notification) => (viewNotifications[notification.id] = notification));
}

function getWorkbookConfigFromPageData() {
  return document.querySelector<Data<WorkbookConfigurationDto>>('#config-workbook')?.value;
}

export const workbookSlice = createSlice({
  name: SLICE_PREFIX,
  // Make a copy because we'll reuse it when we want to reset state, and if not, it will be patched by immer and made immutable
  initialState: createInitialState(),
  reducers: {
    // Should be a thunk, but too complicated for now
    updateWorkbookMetadata(
      state,
      {
        payload: {
          workbookNameAndHash = state.workbookNameAndHash,
          printName = state.printName,
          version = state.version,
          publicationStage = state.publicationStage,
          saved = state.saved,
          changed = state.changed,
          clientChanged = state.clientChanged,
          privileges = state.privileges,
          workbookType = state.workbookType,
          activeWorksheetId = state.activeWorksheetId,
          reportMode = state.reportMode,
        },
      }: PayloadAction<Partial<WorkbookState>>,
    ) {
      if (saved && state.saved) {
        changed = 'no';
        clientChanged = false;
      }
      if (!isRepositoryPublishingProcessEnabled(state)) {
        publicationStage = undefined;
      }
      Object.assign(state, {
        workbookNameAndHash,
        printName,
        saved,
        changed,
        clientChanged,
        version,
        publicationStage,
        privileges,
        workbookType,
        activeWorksheetId,
        reportMode,
      });
    },
    updateWorkbookChanged(state, { payload: { changed = state.changed, clientChanged = state.clientChanged } }) {
      Object.assign(state, {
        changed,
        clientChanged,
      });
    },
    updateViewMetadata: {
      prepare: (viewId: ViewId, metadata: ViewMetadata) => ({ payload: { viewId, metadata } }),
      reducer: (
        state,
        {
          payload: { viewId, metadata },
        }: PayloadAction<{
          viewId: ViewId;
          metadata: ViewMetadata;
        }>,
      ) => {
        let viewMetadata = state.viewMetadataById[viewId];
        if (!viewMetadata) {
          viewMetadata = {};
          state.viewMetadataById[viewId] = viewMetadata;
        }
        Object.entries(metadata).forEach(([key, value]) => {
          if (value !== undefined) {
            viewMetadata[key] = value;
          } else {
            delete viewMetadata[key];
          }
        });
      },
    },
    removeObjectTypes(state, { payload }) {
      Object.assign(state, removeObjecttypesById(state, payload));
    },
    ...panelReducers,
    ...liveModeReducers,
    ...mapViewReducers,
    activateView(state, { payload: viewId }) {
      activateViewInternally(state, viewId);
    },
    maximizeView(state, { payload: viewId }) {
      state.maximizedViewId = viewId;
    },
    forceMaximizeView(state, { payload: viewId }: { payload: ViewId | undefined }) {
      if (viewId) {
        state.forceMaximizePreviousState = {
          maximizedViewId: state.maximizedViewId,
          // cloneDeep() cannot be replaced with structuredClone() in this case,
          // because that fails to clone the Immer proxies.
          panelById: cloneDeep(state.panelById),
        };

        state.maximizedViewId = viewId;
        forcePanelsHidden(state);
      } else {
        Object.assign(state, state.forceMaximizePreviousState);
        delete state.forceMaximizePreviousState;
      }
    },
    initializeWorksheet(state) {
      activateOnlyViewIfAvailable(state);
    },
    unmarkViewAsDirty(state, { payload: viewId }) {
      const view = getViewById(state, viewId);
      const dataViewIds = view.dataViews;

      state.invalidViewsOrDataViews = state.invalidViewsOrDataViews.filter(
        (viewIdOrDataViewId) =>
          view.id !== viewIdOrDataViewId && !dataViewIds.includes(viewIdOrDataViewId as DataViewId),
      );
      state.dataViewsAffectedBySelectionChange = state.dataViewsAffectedBySelectionChange.filter(
        (dataViewId) => !dataViewIds.includes(dataViewId),
      );
    },
    invalidateViewsOrDataViews(state, { payload: viewOrDataViewIds }: PayloadAction<(ViewId | DataViewId)[]>) {
      viewOrDataViewIds.forEach((viewOrDataViewId) => {
        if (!state.invalidViewsOrDataViews.includes(viewOrDataViewId)) {
          state.invalidViewsOrDataViews.push(viewOrDataViewId);
        }
      });
    },
    invalidateDataViewsUsingObjecttypes(state, { payload: objecttypeIds }) {
      const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttypes(state, objecttypeIds);

      for (const dataViewId of dataViewIdsUsingObjecttype) {
        if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
          state.invalidViewsOrDataViews.push(dataViewId);
        }
      }
    },
    invalidateDataViewsUsingObjecttype(state, { payload: objecttypeId }) {
      const dataViewIdsUsingObjecttype = getDataViewIdsUsingObjecttype(state, objecttypeId);

      for (const dataViewId of dataViewIdsUsingObjecttype) {
        if (!state.invalidViewsOrDataViews.includes(dataViewId)) {
          state.invalidViewsOrDataViews.push(dataViewId);
        }
      }
    },
    updateObjecttypeBoundState(state, { payload: { objecttypeId, boundState } }) {
      const objecttype = slice(state).objecttypeById[objecttypeId];
      objecttype.boundState = boundState;
    },
    updateObjecttypeBoundStateOnSave(state) {
      Object.values(slice(state).objecttypeById).forEach((objecttype) => {
        if (objecttype.boundState === 'TRANSIENT') {
          objecttype.boundState = 'BOUND_TO_WORKBOOK';
        }
      });
    },
    updateWorkbookPrintName(state, { payload }) {
      state.printName = payload;
    },
    removeNotifications(
      state,
      { payload: { viewId, removedIds } }: { payload: { viewId: ViewId; removedIds: WorkbookNotificationId[] } },
    ) {
      const viewNotifications = getNotificationsForViewByNotificationId(state, viewId);
      removedIds.forEach((removedId) => delete viewNotifications[removedId]);
    },
    removeNotificationsBySource(
      state,
      { payload: { viewId, source } }: { payload: { viewId: ViewId; source: NotificationSource } },
    ) {
      const notificationsById = state.notificationsByView[viewId] || {};
      Object.values(notificationsById).forEach((notification) => {
        if (notification.source === source) {
          delete notificationsById[notification.id];
        }
      });
    },
    addNotifications(
      state,
      { payload: { viewId, added } }: { payload: { viewId: ViewId; added: WorkbookNotification[] } },
    ) {
      doAddNotifications(state, viewId, added);
    },
    resetActiveLayerId(state) {
      delete state.activeLayerId;
    },
    removeUnusedObjecttypeIds(state, { payload: { objecttypeIds } }) {
      state.unavailableObjecttypeIds = state.unavailableObjecttypeIds.filter((u) => !objecttypeIds.includes(u));
    },
    loadOnDemandWorksheet(state: WorkbookState) {
      state.isOnDemandWorksheetLoading = true;
    },
  },
  // The "pending", "fulfilled" and "rejected" actions are dispatched automatically.
  // Hence, the reducers go in a separate structure.
  extraReducers: (builder) => {
    builder
      .addCase(fetchInitialState.pending, (state) => {
        state.initializationStatus = STATUS.PENDING;
      })
      .addCase(fetchInitialState.fulfilled, (state, action) => {
        // We reinitialize the state to the initial state and set the current workbookNameAndHash on top
        // (we don't want the initial workbookNameAndHash), this is necessary when the Save as actions is triggered
        // from a workbook because we really want to reinitialize the store from scratch
        Object.assign(state, createInitialState(), {
          workbookNameAndHash: action.payload.workbookNameAndHash,
          activeWorksheetId: state.activeWorksheetId,
          // The following properties are not coming from the backend, so we don't want to overwrite them
          printName: state.printName,
          clientChanged: state.clientChanged,
          saved: state.saved,
          changed: state.changed,
          maximizedViewId: state.maximizedViewId,
          viewMetadataById: state.viewMetadataById,
        });
        state.initializationStatus = STATUS.SUCCEEDED;
        if (action.meta.arg?.secondaryStoreMerge) {
          state.lastCommand = undefined;
        }
        setStateFromUpdate(state, action);
        patchZoomRelatedViewsState(state, action.payload);
        initializeOverviewMapContextsByMainMapViewId(state);
      })
      .addCase(fetchInitialState.rejected, (state) => {
        state.initializationStatus = STATUS.FAILED;
      })
      .addCase(fetchPartialState.pending, (state) => {
        state.commandStatus = STATUS.PENDING;
      })
      .addCase(fetchPartialState.fulfilled, (state, action) => {
        state.commandStatus = STATUS.SUCCEEDED;
        const { workbookNameAndHash, operations } = action.payload;
        Object.assign(state, updateWorkbookState(state, { patch: operations }), { workbookNameAndHash });
        state.commandErrorKey = '';
        state.commandErrorDetail = null;
      })
      .addCase(fetchPartialState.rejected, (state) => {
        state.commandStatus = STATUS.FAILED;
      })
      .addCase(resetState.fulfilled, (state) => {
        resetToEmpty(state);
      })
      .addCase(resetState.rejected, () => {
        logger.error('Could not restore redux state!');
      })
      .addCase(executeCommand.pending, (state, action) => {
        state.commandStatus = STATUS.PENDING;
        // Whenever a command is executed, stop live mode.
        stopLiveModeByOther(state);
        if (isActiveWorksheetSettingOnDemand(state)) {
          const command = action.meta.arg;
          const isOnDemandRelevantCommand = shouldOnDemandBeConsidered(state, command);
          state.isOnDemandWorksheetLoading = !isOnDemandRelevantCommand;
        }
      })
      .addCase(executeCommand.fulfilled, (state, action) => {
        state.commandStatus = STATUS.SUCCEEDED;
        // cloneDeep() cannot be replaced with structuredClone() in this case,
        // because that fails to clone the Immer proxies.
        const previousActiveViewState = cloneDeep(getActiveView(state));
        const {
          workbookNameAndHash,
          operations,
          invalidViewsOrDataViews,
          invalidLayerIdsByViewId,
          dataViewsAffectedBySelectionChange,
        } = action.payload;
        Object.assign(
          state,
          updateWorkbookState(state, {
            patch: operations,
          }),
          { workbookNameAndHash },
        );
        state.lastCommand = action.meta.arg;
        updateDataViewInvalidationState(
          state,
          invalidViewsOrDataViews,
          invalidLayerIdsByViewId,
          dataViewsAffectedBySelectionChange,
        );
        updateActiveLayerId(state, previousActiveViewState, state.lastCommand!);
        unsetMaximizedViewIfViewDoesNotExist(state, state.lastCommand!);

        const viewIds = new Set(Object.keys(state.viewById));
        state.viewMetadataById = Object.fromEntries(
          Object.entries(state.viewMetadataById).filter(([viewId]) => viewIds.has(viewId)),
        );

        patchMapViewState(state, action.payload);

        state.commandErrorKey = '';
        state.commandErrorDetail = null;
      })
      .addCase(executeCommand.rejected, (state, action) => {
        const problem = action.payload as Problem | undefined;
        // if the actual error is swallowed (our default) then the `payload` is `undefined`
        state.commandErrorKey = problem?.key || '';
        state.commandErrorDetail = createCommandErrorDetails(problem);
        state.commandStatus = STATUS.FAILED;
      })
      .addCase(fetchDataDependencyState.fulfilled, (state, action) => {
        updateObjecttypeDataDependenciesState(state, action.payload);
      });
    addTextViewExtraReducers(builder);
  },
});

/**
 * Updates the given workbook state with a patch (command response) from the server.
 *
 * @param state - The state object to update
 * @param context
 * @param context.patch - A JSON patch
 * @return The updated state object
 */
function updateWorkbookState(state: WorkbookState, { patch }: { patch: Operation[] }) {
  const nextState = applyPatch(state, patch).newDocument;
  const activeWorksheet = getActiveWorksheet(state);
  if (
    !activeWorksheet ||
    (isWorksheetOnDemand(activeWorksheet) &&
      shouldOnDemandBeConsidered(state, slice(state).lastCommand) &&
      hasDirtyViews(nextState))
  ) {
    state.activeViewId = undefined;
  } else {
    activateOnlyViewIfAvailable(state);
  }
  validateViews(nextState);
  return nextState;
}

function updateObjecttypeDataDependenciesState(
  state: WorkbookState,
  objecttypeDataDependencies: ObjecttypesDataDependenciesDto,
) {
  state.objecttypeDataDependencies = objecttypeDataDependencies;
}

function hasDirtyViews(nextState: WorkbookState) {
  return nextState.invalidViewsOrDataViews.length > 0;
}

/**
 * Activates the only view of the active worksheet if the designer is visible.
 */
function activateOnlyViewIfAvailable(state: WorkbookState) {
  const views = getActiveWorksheetViews(state);
  if (isPanelVisible(state, 'd-view-settings-panel') && views.length === 1) {
    state.activeViewId = views[0].id;
  }
}

function removeObjecttypesById(
  {
    objecttypeById,
    attributeById,
    dataSources,
  }: Pick<WorkbookState, 'objecttypeById' | 'attributeById' | 'dataSources'>,
  objecttypeIdsToRemove: ObjecttypeIdString[],
) {
  assert(Array.isArray(objecttypeIdsToRemove), 'objecttypeIdsToRemove has to be an array');
  // remove object types and their attributes
  for (const objecttypeId of objecttypeIdsToRemove) {
    if (!objecttypeById[objecttypeId]) {
      continue;
    }
    const attributeIds = objecttypeById[objecttypeId].attributes;
    for (const attributeId of attributeIds) {
      delete attributeById[attributeId];
    }
    // If we removed enrichment object type we have to do cleanup.
    // Code below removes orphan references to deleted enrichment stored in enriched objecttypes enrichment array.
    const enrichments = objecttypeById[objecttypeId].enrichments;
    if (enrichments) {
      const enrichmentIds = enrichments.map((enrichment) => enrichment.objecttypeId);
      removeObjecttypesById({ objecttypeById, attributeById, dataSources }, enrichmentIds);
    } else {
      // this removes all the references
      for (const objecttype of Object.values(objecttypeById)) {
        if (objecttype.enrichments) {
          objecttype.enrichments = objecttype.enrichments.filter(
            (enrichment) => !objecttypeIdsToRemove.includes(enrichment.objecttypeId),
          );
        }
      }
    }
    delete objecttypeById[objecttypeId];
  }

  // remove obsolete dataSources
  const uniqueDataSourceIds = new Set(Object.values(objecttypeById).map(({ dataSourceName }) => dataSourceName));
  const newDataSources = dataSources.filter(({ itemId }) => uniqueDataSourceIds.has(itemId.itemName));

  return { objecttypeById, attributeById, dataSources: newDataSources };
}

const { reducer, actions } = workbookSlice;
getStore().registerReducer(SLICE_PREFIX, reducer);

// Export reducer for testing
export { reducer };

// Action creators

export const {
  updateWorkbookMetadata,
  updateWorkbookChanged,
  updateViewMetadata,
  removeObjectTypes,
  togglePanelVisibility,
  hideAllPanels,
  activateView,
  maximizeView,
  forceMaximizeView,
  initializeWorksheet,
  unmarkViewAsDirty,
  invalidateViewsOrDataViews,
  invalidateDataViewsUsingObjecttype,
  invalidateDataViewsUsingObjecttypes,
  updateObjecttypeBoundState,
  updateObjecttypeBoundStateOnSave,
  updateWorkbookPrintName,
  removeNotifications,
  removeNotificationsBySource,
  addNotifications,
  resetActiveLayerId,
  removeUnusedObjecttypeIds,
  refreshViewData,
  startLiveMode,
  stopLiveMode,
  removeZoomRelatedViewId,
  setTargetMapExtentAndScaleForCoupledView,
  synchronizeToLeftTopMostView,
  stopInteractionForCoupledView,
  setMainMapContextForOverviewMap,
  setMainMapExtentForOverviewMapViewId,
  loadOnDemandWorksheet,
} = actions;

export const deactivateView = () => activateView('');
export const minimizeView = () => maximizeView('');

// Selectors

export const getWorkbookConfiguration = (state: RootStateOr<WorkbookState>) => slice(state).workbookConfiguration;
export const getCouplingLayerLimit = (state: RootStateOr<WorkbookState>) =>
  slice(state).workbookConfiguration!.restrictions.couplingLayerLimit;
export const getInitializationStatus = (state: RootStateOr<WorkbookState>) => slice(state).initializationStatus;
export const getCommandStatus = (state: RootStateOr<WorkbookState>) => slice(state).commandStatus;
export const getObjecttypeDataDependencies = (state: RootStateOr<WorkbookState>) =>
  slice(state).objecttypeDataDependencies;

export const getWorkbookName = (state: RootStateOr<WorkbookState>) => slice(state).workbookName;
export const getWorkbookNameAndHash = (state: RootStateOr<WorkbookState>) => {
  const workbookNameAndHash = slice(state).workbookNameAndHash;
  assertNonNullable(workbookNameAndHash);
  return workbookNameAndHash;
};
export const getWorkbookVersion = (state: RootStateOr<WorkbookState>) => slice(state).version;
export const getWorkbookPublicationStage = (state: RootStateOr<WorkbookState>) => slice(state).publicationStage;
export const isWorkbookPublished = (state: RootStateOr<WorkbookState>) =>
  getWorkbookPublicationStage(state) === 'published';
export const getActiveWorksheetId = (state: RootStateOr<WorkbookState>) => slice(state).activeWorksheetId!;
export const getWorksheetById = (state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) =>
  slice(state).worksheetById[worksheetId];

export const isRepositoryPublishingProcessEnabled = (state: RootStateOr<WorkbookState>) =>
  slice(state).repository?.publishingProcessEnabled ?? false;

export const isWorkbookDeletionDeadlineEnabled = (state: RootStateOr<WorkbookState>) =>
  slice(state).repository?.deletionDeadlineWorkbookEnabled ?? false;

/**
 * Returns all worksheets of the workbook.
 */
export const getWorksheets = (state: RootStateOr<WorkbookState>) => {
  const worksheetIds = slice(state).worksheets;
  return worksheetIds.map((id) => getWorksheetById(state, id));
};

/**
 * <p>
 *     <b>Important note</b><br/>
 *     Be aware that the concept of active worksheet does not exist in reporting, the
 *     method will return <code>undefined</code>. Consider this in direct calls as well
 *     as selectors that make use of this. It is safer to use {@link getWorksheetByViewId}
 *     if you have access to the viewId.
 * </p>
 * @param state
 */
export const getActiveWorksheet = (state: RootStateOr<WorkbookState>) =>
  getWorksheetById(state, getActiveWorksheetId(state));
export const isActiveWorksheetOnDemand = (state: RootStateOr<WorkbookState>) => {
  return isActiveWorksheetSettingOnDemand(state) && !slice(state).isOnDemandWorksheetLoading && !isReportMode(state);
};
export const getWorksheetByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getWorksheets(state).find((worksheet) => worksheet.views.includes(viewId));

export const getWorkbookRepositoryName = (state: RootStateOr<WorkbookState>) => slice(state).repository!.name;
export const workbookRepositoryHasCmdb = (state: RootStateOr<WorkbookState>) =>
  slice(state).repository?.cadenzaManagedDB ?? false;
export const getWorkbookRepository = (state: RootStateOr<WorkbookState>) => slice(state).repository;
export const getWorkbookId = (state: RootStateOr<WorkbookState>): WorkbookId =>
  WorkbookId.of(getWorkbookRepositoryName(state), getWorkbookName(state));
export const getWorkbookPrivileges = (state: RootStateOr<WorkbookState>) => slice(state).privileges;
export const getWorkbookPrintName = (state: RootStateOr<WorkbookState>) => slice(state).printName;
export const getWorkbookType = (state: RootStateOr<WorkbookState>) => slice(state).workbookType;
export const getWorkbookRelation = (state: RootStateOr<WorkbookState>) => slice(state).relation;
export const isWorkbookSaved = (state: RootStateOr<WorkbookState>) => slice(state).saved;
export const isWorkbookChanged = (state: RootStateOr<WorkbookState>) =>
  slice(state).changed !== 'no' || slice(state).clientChanged;
export const hasMajorChange = (state: RootStateOr<WorkbookState>) => slice(state).changed === 'major';
export const isWorkbookVisible = (state: RootStateOr<WorkbookState>) =>
  hasPrivilege(slice(state).privileges, 'visible');
export const isWorkbookVisibleRestricted = (state: RootStateOr<WorkbookState>) =>
  hasPrivilege(slice(state).privileges, 'visibleRestricted');
export const isWorkbookSharable = (state: RootStateOr<WorkbookState>) => hasPrivilege(slice(state).privileges, 'share');
export const isWorkbookWritable = (state: RootStateOr<WorkbookState>) => hasPrivilege(slice(state).privileges, 'write');
export const isWorkbookVisibilityRestricted = (state: RootStateOr<WorkbookState>) =>
  isVisibilityRestricted(slice(state).privileges); // Also see WorkbookContextService.isWorkbookVisibilityRestricted - Same implementation in frontend and backend
export const getAttributeDescriptionVisibility = (state: RootStateOr<WorkbookState>): AttributeDescriptionVisibility =>
  slice(state).config!.attributeDescriptionVisibility;
export const areRequiredConditionsValid = createSelector(
  getActiveWorksheet,
  (worksheet) => worksheet.requiredConditionsValid,
);

/**
 * Checks if the workbook is empty.
 * _Note_: Here, empty refers to having no views, no data sources, no object types and only one worksheet.
 * Workbook settings like drillthrough targets, the worksheet names etc. are not taken into account here.
 */
export const isWorkbookEmpty = (state: RootStateOr<WorkbookState>) =>
  getViewIds(state).length === 0 &&
  getDataSources(state).length === 0 &&
  getObjecttypes(state).length === 0 &&
  getWorksheets(state).length === 1;

/**
 * Returns the workbook specific drill-through targets (not including the legacy targets
 * defined via Cadenza Desktop)
 */
export const getDrillThroughTargets = (state: RootStateOr<WorkbookState>) => slice(state).drillThroughTargets;

/**
 * Returns all curated reports
 *
 * @param state - The state object
 * @return All reports
 */
export const getReports = createSelector((state: RootStateOr<WorkbookState>) => slice(state).reportById, Object.values);

/**
 * Returns all workbook embedding targets
 *
 * @param state - The state object
 * @return All embedding targets
 */
export const getEmbeddingTargets = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).embeddingTargetById,
  Object.values,
);

/**
 * Returns workbook embedding target with given ID
 *
 * @param state - The state object
 * @param id
 * @return WorkbookEmbeddingTargetDto
 */
export const getEmbeddingTargetById = (state: RootStateOr<WorkbookState>, id: WorkbookEmbeddingTargetId) =>
  slice(state).embeddingTargetById[id];

/**
 * Return ReportDTO with given ID
 */
export const getReportById = (state: RootStateOr<WorkbookState>, reportId: ReportId) =>
  slice(state).reportById[reportId];

/**
 * Returns the active worksheets background color if it is set, otherwise the first set background
 * color of any previous worksheet ("auto"-setting). The default is the background color set in theming.
 * If worksheetId is passed, returns the background color of the worksheet with worksheetId.
 *
 * @param state - The state object.
 * @param [worksheetId] - Optional: return background color of worksheet with worksheetId.
 * @return The calculated background color of the worksheet.
 */
export const getWorksheetBackgroundColor = (state: RootStateOr<WorkbookState>, worksheetId?: WorksheetId): HexColor => {
  const worksheets = getWorksheets(state);
  const worksheet = worksheetId ? worksheets.find((ws) => ws.id === worksheetId) : getActiveWorksheet(state);
  if (worksheet) {
    const getWsBackgroundColor = (ws: WorksheetDto) => {
      // Get worksheet background color without "auto"
      return getSetting<HexColor>(ws, WorksheetSettingsKey.toPersistenceKey('WORKSHEET_BACKGROUND_COLOR'));
    };

    let color = getWsBackgroundColor(worksheet);
    if (color != null) {
      return color;
    }

    const worksheetIndex = worksheets.map((ws) => ws.id).indexOf(worksheet.id);
    const previousWorksheets = worksheets.slice(0, worksheetIndex);
    while (previousWorksheets.length > 0) {
      color = getWsBackgroundColor(previousWorksheets.pop()!);
      if (color != null) {
        return color;
      }
    }
  }
  return theme('worksheet-bg');
};

export const getWorksheetViewBorders = (state: RootStateOr<WorkbookState>, worksheetId?: WorksheetId) => {
  return calculateWorksheetViewBorders(worksheetId ?? getActiveWorksheetId(state), state);
};

export const getAttributes = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).attributeById);
export const getAttributeById = (state: RootStateOr<WorkbookState>, attributeId: AttributeId | ActiveAttributeId) => {
  return (
    slice(state).activeAttributeById[attributeId as ActiveAttributeId] ||
    slice(state).attributeById[attributeId as AttributeId]
  );
};
export const getAttributesById = (
  state: RootStateOr<WorkbookState>,
  attributeIds: (AttributeId | ActiveAttributeId)[],
) => {
  return attributeIds.map((attrId) => getAttributeById(state, attrId));
};

export const getAttributesByBundle = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const dataView = getDataViewById(state, dataViewId);
  if (dataView) {
    return Object.fromEntries(
      Object.entries(dataView.attributes).map(([bundle, attributeIds]) => [
        bundle,
        attributeIds.map((attributeId) => getAttributeById(state, attributeId)),
      ]),
    ) as Record<AttributeBundleName, WorkbookAttributeDto[]> | undefined;
  }
};

export const getAttributeByName = (state: RootStateOr<WorkbookState>, attributeName: string) => {
  return getAttributes(state).find((attr) => attr.name === attributeName);
};

export const getAttributeIdsFromBundle = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  const dataView = getDataViewById(state, dataViewId);
  return dataView.attributes[bundleName] ?? [];
};

export function hasBundleAttributes(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) {
  return getAttributeIdsFromBundle(state, dataViewId, bundleName).length !== 0;
}

export const getAttributesFromBundles = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  ...bundleNames: AttributeBundleName[]
) => {
  const dataView = getDataViewById(state, dataViewId);
  return bundleNames
    .flatMap((bundleName) => dataView?.attributes[bundleName] || [])
    .map((attributeId) => getAttributeById(state, attributeId));
};

export function getAttributeSetting(
  state: RootStateOr<WorkbookState>,
  attributeId: ActiveAttributeId,
  persistenceKey: string,
  defaultValue?: string,
) {
  return getSetting(getAttributeById(state, attributeId), persistenceKey, defaultValue);
}

// This selector returns all object types, including ones with restricted visibility.
// In most cases only visible unrestricted object types are required, so use getVisibleObjectTypes or getVisibleObjecttypeIds instead.
export const getObjecttypes: (state: RootStateOr<WorkbookState>) => Objecttype[] = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).objecttypeById,
  Object.values,
);

export const getUnavailableObjecttypeIds = (state: RootStateOr<WorkbookState>) => slice(state).unavailableObjecttypeIds;

const getUsedAttributes = createSelector(
  [
    (state) => slice(state).dataViewById,
    (state) => slice(state).conditionById,
    (state) => slice(state).drillThroughTargets,
  ],
  (dataViewById, conditionById, drillThroughTargets) =>
    new Set([
      ...Object.values(dataViewById).flatMap((dv) => Object.values(dv.attributes).flat().map(getBaseAttributeId)),
      ...Object.values(conditionById).flatMap((c) => [
        ...c.attributeIds,
        ...(c.conditionConfiguration && 'additionalAttributes' in c.conditionConfiguration ?
          c.conditionConfiguration.additionalAttributes
        : []),
      ]),
      ...Object.values(drillThroughTargets).map((dt) => dt.attributeId),
    ]),
);

export const isObjecttypeUsed = (state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) =>
  [...getUsedAttributes(state)].some((id) => isPartOfObjecttype(id, objecttypeId)) ||
  Object.values(slice(state).dataViewById).some((dataView) => dataView.mainObjecttype.objecttypeId === objecttypeId);

export const isAttributeUsed = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) =>
  getUsedAttributes(state).has(attributeId);

export const getDataProtection = (state: RootStateOr<WorkbookState>) => {
  return slice(state).dataProtection;
};
export const getObjecttypeById = (state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) => {
  return slice(state).objecttypeById[objecttypeId];
};
export const getAllAttributesFromObjectType = createSelector(
  [getObjecttypeById, (state) => slice(state).attributeById, (state) => slice(state).objecttypeById],
  (objecttype, attributesById, objecttypeById) => {
    const attributes = objecttype.attributes.map((attributeId) => attributesById[attributeId]);
    const attributesFromEnrichments =
      objecttype.enrichments?.flatMap((enrichment) => {
        const enrichmentObjecttype = objecttypeById[enrichment.objecttypeId];
        return enrichmentObjecttype.attributes.map((attributeId) => attributesById[attributeId]);
      }) ?? [];
    attributes.push(...attributesFromEnrichments);
    return attributes;
  },
);

export const getAllEnrichmentsFromObjectType = createSelector(
  [getObjecttypeById],
  (objecttype) => objecttype?.enrichments ?? [],
);

export const getAllEnrichmentObjecttypeIds = createSelector([(state) => slice(state).objecttypeById], (objecttypes) =>
  Object.values(objecttypes)
    .flatMap((ot) => ot.enrichments ?? [])
    .map((enrichment) => enrichment.objecttypeId),
);

export const getVisibleObjecttypes = createSelector(getObjecttypes, (objecttypes) =>
  objecttypes
    .filter((objecttype) => objecttype.visible && !isVisibilityRestricted(objecttype.currentUserPrivileges))
    .sort((a, b) => compareWithLocale(a.printName, b.printName)),
);
export const getVisibleObjecttypeIds = createSelector(getVisibleObjecttypes, (visibleObjecttypes) =>
  visibleObjecttypes.map(({ id }) => id),
);
export const getObjecttypesWithAttributes = createSelector(getObjecttypes, resolveObjecttypeAttributes);
export const getVisibleObjecttypesWithoutTheirEnrichments = createSelector(getVisibleObjecttypes, (objecttypes) => {
  const visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds = [
    ...new Set(
      objecttypes.flatMap((objecttype) => objecttype.enrichments ?? []).map((enrichment) => enrichment.objecttypeId),
    ),
  ];
  return objecttypes
    .filter((objecttype) => !visibleEnrichmentsOnVisibleObjecttypeObjecttypeIds.includes(objecttype.id))
    .sort((a, b) => compareWithLocale(a.printName, b.printName));
});

function isVisibilityRestricted(privileges: ObjectPrivilege[]) {
  return privileges?.length > 0 && !hasPrivilege(privileges, 'visible');
}

const getObjecttypesByAttributeId = createSelector(getObjecttypes, (objecttypes) =>
  Object.fromEntries(
    objecttypes.flatMap((objecttype) => objecttype.attributes.map((attributeId) => [attributeId, objecttype])),
  ),
);

export const getObjecttypeByAttributeId = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) => {
  return getObjecttypesByAttributeId(state)[attributeId];
};

const getEnrichmentsByAttributeId = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).objecttypeById,
  (objecttypeById) => {
    return Object.fromEntries(
      Object.values(objecttypeById)
        .flatMap((objecttype) => objecttype.enrichments)
        .flatMap((enrichment) =>
          enrichment ?
            objecttypeById[enrichment.objecttypeId].attributes.map((attributeId) => [attributeId, enrichment])
          : [],
        ),
    );
  },
);

export const getEnrichmentByAttributeId = (state: RootStateOr<WorkbookState>, attributeId: AttributeId) => {
  return getEnrichmentsByAttributeId(state)[attributeId];
};

/**
 * Returns, in case the attributeId belongs to an objecttype which is an enrichment,
 * the enrichedObjecttypeId of this enrichment. Else, the objecttypeId for this attributeId
 */
export const getEnrichedObjecttypeOrObjecttypeIdByAttributeId = (
  state: RootStateOr<WorkbookState>,
  attributeId: AttributeId,
) => {
  const enrichment = getEnrichmentByAttributeId(state, attributeId);
  if (enrichment) {
    return enrichment.enrichedObjecttypeId;
  }
  return getObjecttypeByAttributeId(state, attributeId).id;
};

export const hasObjecttypeWithObjectId = (
  state: RootStateOr<WorkbookState>,
  objecttypeIdString: ObjecttypeIdString,
) => {
  return !!getObjecttypeById(state, objecttypeIdString);
};

export const hasObjecttypeWithPrintName = (state: RootStateOr<WorkbookState>, name: string) => {
  return getObjecttypes(state).some((ot) => ot.printName === name);
};

export const hasBoundObjecttypes = (state: RootStateOr<WorkbookState>) => {
  return getObjecttypes(state).some((ot) => isObjecttypeBound(ot));
};

export const getDataSources = (state: RootStateOr<WorkbookState>) => slice(state).dataSources;

export const getVisibleDataSources = createSelector(getDataSources, getVisibleObjecttypes, filterEmptyDatasources);

function filterEmptyDatasources(dataSources: WorkbookDataSourceDto[], visibleObjecttypes: Objecttype[]) {
  return dataSources.filter(({ itemId }) =>
    visibleObjecttypes.some(({ dataSourceName }) => dataSourceName === itemId.itemName),
  );
}

export function getDataSourceByObjecttypeId(state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) {
  const objecttype = getObjecttypeById(state, objecttypeId);
  return getDataSources(state).find(
    (dataSource) =>
      dataSource.itemId.repositoryName === objecttype.dataSourceRepositoryName &&
      dataSource.itemId.itemName === objecttype.dataSourceName,
  )!;
}

export function hasObjecttypeDatasourceCapabilities(
  state: RootStateOr<WorkbookState>,
  objecttypeId: ObjecttypeIdString,
  capabilities: DataSourceCapability[],
) {
  const dataSource = getDataSourceByObjecttypeId(state, objecttypeId);
  return capabilities.every((capability) => hasCapability(dataSource, capability));
}

export function getProjectById(state: RootStateOr<WorkbookState>, projectId: ProjectId) {
  return slice(state).projectById[itemIdToString(projectId)];
}

/** Returns the workbook's ProjectId */
export function getProject(state: RootStateOr<WorkbookState>) {
  const projectId = slice(state).project;
  return projectId && getProjectById(state, projectId);
}

export function isObjecttypeInCmdb(state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) {
  return getDataSourceByObjecttypeId(state, objecttypeId).cmdb;
}

export const getConditionsAndGroups = (state: RootStateOr<WorkbookState>) => slice(state).conditionsAndGroups;
export const getConditionById = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  slice(state).conditionById[conditionId];

const getConditionsByGroupId = (state: RootStateOr<WorkbookState>, conditionGroupId: ConditionGroupId) =>
  getConditionGroupConditionIds(state, conditionGroupId).map((conditionId) => getConditionById(state, conditionId));

export const getConditionDeactivated = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  getDeactivated(getConditionById(state, conditionId));

export const isConditionDeactivatedForActiveWorksheet = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isDeactivatedForWorksheet(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionHidden = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isHidden(getConditionById(state, conditionId), getActiveWorksheetId(state));

export const isConditionGroupHidden = (state: RootStateOr<WorkbookState>, conditionGroupId: ConditionGroupId) =>
  areAllHidden(getConditionsByGroupId(state, conditionGroupId), getActiveWorksheetId(state));

export const isConditionMandatory = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  isMandatory(getConditionById(state, conditionId));

export function getAffectedViewsByHierarchy(state: RootStateOr<WorkbookState>) {
  return slice(state).affectedViewsByHierarchyId;
}

function getConditionsInAnalysisContext(state: RootStateOr<WorkbookState>) {
  const result: ConditionId[] = [];
  for (const id of slice(state).conditionsAndGroups) {
    result.push(
      ...(slice(state).conditionById[id as ConditionId] ?
        [id as ConditionId]
      : slice(state).conditionGroupById[id as ConditionGroupId].conditions),
    );
  }
  return result;
}

export function isAnalysisContextAffectingViews(state: RootStateOr<WorkbookState>) {
  return getConditionsInAnalysisContext(state).some((conditionId) => {
    const affectedViewIds = getViewIdsAffectedByCondition(getStore().getState(), conditionId);
    if (affectedViewIds.length > 0) {
      const condition = slice(state).conditionById[conditionId];
      return hasValuesSet(condition) && !isConditionDeactivatedForActiveWorksheet(state, conditionId);
    }
  });
}

export function getViewIdsAffectedByCondition(
  state: RootStateOr<WorkbookState>,
  conditionId: ConditionId,
  withLocalConditions = false,
) {
  return (
    slice(state).affectedViewsByConditionId[conditionId]?.[
      withLocalConditions ? 'affectedViewsWithLocalConditions' : 'affectedViews'
    ]?.[getActiveWorksheetId(state)] ?? []
  );
}

function getDataViewIdsAffectedByCondition(state: RootStateOr<WorkbookState>, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]?.affectedDataViews?.[getActiveWorksheetId(state)] ?? [];
}

export function getAttributesByConditionId(state: RootStateOr<WorkbookState>, conditionId: ConditionId) {
  return slice(state).affectedViewsByConditionId[conditionId]?.affectedAttributesByDataViewId ?? {};
}

export const getConditionsById = (state: RootStateOr<WorkbookState>) => slice(state).conditionById;
export const getConditionGroupById = (state: RootStateOr<WorkbookState>, conditionId: ConditionGroupId) =>
  slice(state).conditionGroupById[conditionId];
export const getConditionGroups = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).conditionGroupById);

export const getConditionGroupConditionIds = (state: RootStateOr<WorkbookState>, conditionId: ConditionGroupId) =>
  getConditionGroupById(state, conditionId).conditions;

export const getHierarchies = (state: RootStateOr<WorkbookState>) => Object.values(slice(state).hierarchyById);

export const isGlobalCondition = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) =>
  [...slice(state).conditionsAndGroups, ...getConditionGroups(state).flatMap((group) => group.conditions)].includes(
    conditionId,
  );

export const getGlobalConditionIds = (state: RootStateOr<WorkbookState>) => {
  return Object.keys(getConditionsById(state))
    .map((key) => key as ConditionId)
    .filter((conditionId) => isGlobalCondition(state, conditionId));
};

export const getGlobalConditionsHavingValue = (state: RootStateOr<WorkbookState>) => {
  return Object.values(slice(state).conditionById).filter(
    (condition) => isGlobalCondition(state, condition.id) && conditionHasValues(condition),
  );
};

export const hasSpatialConditions = createSelector(getConditionsById, (conditionById) => {
  const conditions = Object.values(conditionById);
  return conditions.some(({ type }) => type === 'PolygonCondition');
});

export const isFilteredByCondition = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  conditionId: ConditionId,
) => {
  return getDataViewIdsAffectedByCondition(state, conditionId).includes(dataViewId);
};

export const hasDataViewConditions = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).conditions.length > 0;
};

export const isDataViewAffectedByWorkbookConditions = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const globalAndLocalConditionsById = getConditionsById(state);
  const globalAndLocalConditionIds = Object.keys(globalAndLocalConditionsById) as ConditionId[];
  const globalConditionIds = globalAndLocalConditionIds.filter((conditionId) => isGlobalCondition(state, conditionId));

  // we check if there's at least one workbook condition that affects our dataView
  return globalConditionIds.some((conditionId) => isFilteredByCondition(state, dataViewId, conditionId));
};

export const getViews = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (items) => Object.values(items) as ViewDto[],
);
const getViewIds = createSelector(
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (viewById) => Object.keys(viewById) as ViewId[],
);
export const getActiveWorksheetViewIds = (state: RootStateOr<WorkbookState>) => getActiveWorksheet(state)?.views ?? [];
export const getActiveWorksheetViews = (state: RootStateOr<WorkbookState>) =>
  getActiveWorksheetViewIds(state).map((viewId) => getViewById(slice(state), viewId));
export const getActiveView = (state: RootStateOr<WorkbookState>) => {
  const activeViewId = getActiveViewId(state);
  if (activeViewId) {
    return getViewById(state, activeViewId);
  }
};

export const getActiveOrFirstMapViewId = createSelector([getActiveView, getViews], (activeView, views) => {
  if (activeView?.type === ViewType.MAP) {
    return activeView.id;
  }
  return views.find((view) => view.type === ViewType.MAP)?.id;
});

export const getViewById = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  addViewMetadata(state, slice(state).viewById[viewId])!;
export const getViewByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  addViewMetadata(
    state,
    getActiveWorksheetViews(state).find((view) => view.dataViews.includes(dataViewId)),
  )!;

export const getViewByLayerId = (state: RootStateOr<WorkbookState>, layerId: LayerId) => {
  const views = isReportMode(state) ? getViews(state) : getActiveWorksheetViews(state);
  return addViewMetadata(
    state,
    views.find((view) => Object.keys(view.layerDefinitions ?? {}).includes(layerId)),
  )!;
};

export const getLayerGroupsFromView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  return Object.values(view.layerDefinitions || []).filter((layer) => layer.structureType !== 'SINGLE');
};

export const getActiveLayerId = (state: RootStateOr<WorkbookState>) => {
  return slice(state).activeLayerId;
};

export const getVisualisationKeyByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (!view) {
    return;
  }
  const dataViewId = view.dataViews[0];
  if (dataViewId) {
    return getDataViewById(state, dataViewId).visualizationKey;
  }
};

export const getVisualizationKeyByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId).visualizationKey;
};

export const getViewNamesByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = slice(state).viewById[viewId];
  return {
    name: view?.name,
    defaultName: view?.defaultName,
  };
};

function addViewMetadata(state: RootStateOr<WorkbookState>, view?: ViewDto): ViewWithMetadata | undefined {
  if (view) {
    return { ...view, metadata: slice(state).viewMetadataById[view.id] ?? {} };
  }
}

export const getViewMetadata = <T extends ViewMetadata = ViewMetadata>(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
) => slice(state).viewMetadataById[viewId] as T | undefined;

export const isViewLoading = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const loadingState = getViewMetadata(state, viewId)?.loadingState as LoadingState;
  return ['LOADING', 'LOADING_LONG'].includes(loadingState);
};

const isViewRendering = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const metadata = getViewMetadata(state, viewId);
  if (metadata && 'isRendering' in metadata) {
    return metadata.isRendering;
  }
  return false;
};

export const isAnyRelevantViewLoading = (state: RootStateOr<WorkbookState>, viewIds: ViewId[]) =>
  viewIds.some((viewId) => isViewLoading(state, viewId));

export const isAnyViewLoadingOrRendering = (state: RootStateOr<WorkbookState>) =>
  getViewIds(state).some((viewId) => isViewLoading(state, viewId) || isViewRendering(state, viewId));

export const getDataViewById = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  slice(state).dataViewById[dataViewId];

export const getOnlyDataViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const dataViewIds = getDataViewIdsByViewId(state, viewId);
  assert(dataViewIds.length === 1, 'expected exactly one DataView');
  return dataViewIds[0];
};

export const getDataViewStatus = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) =>
  slice(state).dataViewById[dataViewId].status;

export function hasWorkbookInvalidStatus() {
  const hasInvalidDataViews = Object.values(slice(getStore().getState()).dataViewById).some(
    (dataView) => dataView.status !== 'VALID',
  );
  const hasInvalidLayers = getViews(getStore().getState())
    .map((viewDto) => (viewDto.layerDefinitions ? Object.values(viewDto.layerDefinitions) : []))
    .flatMap((layerDtos) => layerDtos.flatMap((layerDto) => layerDto.status))
    .some((status) => status !== 'OK');
  const unavailableObjecttypeIds = getUnavailableObjecttypeIds(getStore().getState());
  const hasInvalidAttributes = getAttributes(getStore().getState()).some((attribute) => attribute.status !== 'OK');
  const hasInvalidConditions = getOrderedConditions(getStore().getState())
    .map((condition) => condition.status)
    .some((status) => status !== 'VALID');
  return (
    hasInvalidDataViews ||
    hasInvalidLayers ||
    unavailableObjecttypeIds.length > 0 ||
    hasInvalidAttributes ||
    hasInvalidConditions
  );
}

const getDataViewIdsUsingObjecttypes = (state: RootStateOr<WorkbookState>, objecttypeIds: ObjecttypeIdString[]) => {
  const dataViewIds = [];

  for (const [dataViewId, dataView] of entries(slice(state).dataViewById)) {
    if (
      Object.values(dataView.attributes) // Only bundle attributes
        .flat() // Unwrap bundles attributes
        .some((attributeId) => objecttypeIds.includes(getAttributeById(state, attributeId).objecttypeId))
    ) {
      dataViewIds.push(dataViewId);
    }
  }

  return dataViewIds;
};

function getDataViewIdsUsingObjecttype(state: RootStateOr<WorkbookState>, objecttypeId: ObjecttypeIdString) {
  return getDataViewIdsUsingObjecttypes(state, [objecttypeId]);
}

export const getDataViewIdsByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getViewById(state, viewId).dataViews;

export const getDataViewsByViewId = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getViewById(state, viewId).dataViews.map((dataViewId) => getDataViewById(state, dataViewId));

function getViewIdsByWorksheetId(state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) {
  return getWorksheetById(state, worksheetId).views;
}

function getDataViewsByWorksheetId(state: RootStateOr<WorkbookState>, worksheetId: WorksheetId) {
  return getViewIdsByWorksheetId(state, worksheetId).flatMap((viewId) => getDataViewIdsByViewId(state, viewId));
}

export function getMainObjecttypeIdsForActiveWorksheet(state: RootStateOr<WorkbookState>) {
  const worksheetId = getActiveWorksheetId(state);
  const dataViewIds = getDataViewsByWorksheetId(state, worksheetId);
  const objecttypeIds = dataViewIds
    .map((dataViewId) => getMainObjecttypeIdForDataView(state, dataViewId))
    .filter((objecttypeId) => UNSET_OBJECTTYPE_ID !== objecttypeId);
  return [...new Set(objecttypeIds)];
}

export function getMainObjecttypeIdForDataView(state: RootStateOr<WorkbookState>, dataViewId: DataViewId) {
  return getDataViewById(state, dataViewId).mainObjecttype.objecttypeId;
}

const getNotificationsForViewByNotificationId = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return slice(state).notificationsByView[viewId] ?? {};
};

export const getNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return Object.values(getNotificationsForViewByNotificationId(state, viewId));
};

export const layerHasErrorNotifications = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return getNotificationsForLayer(state, viewId, layerId).some((notification) => notification.level === 'ERROR');
};

export const getNotificationsForViewByTypes = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  types: NotificationType[],
) => {
  return getNotificationsForView(state, viewId).filter((notification) => types.includes(notification.type));
};

const getDataViewNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isDataViewNotification);
};

export const getDataViewNotificationForActiveView = (state: RootStateOr<WorkbookState>) => {
  const activeViewId = getActiveViewId(state);
  return activeViewId ? getDataViewNotificationsForView(state, activeViewId) : [];
};

const getLayerNotificationsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  return getNotificationsForView(state, viewId).filter(isLayerNotification);
};

export const getNotificationsForDataView = (
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  dataViewId: DataViewId,
) => {
  return getDataViewNotificationsForView(state, viewId).filter(
    (notification) => notification.dataViewId === dataViewId,
  );
};

export const getNotificationsForLayer = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return getLayerNotificationsForView(state, viewId).filter((notification) => notification.layerId === layerId);
};

export const getDataViewNamesByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const dataView = slice(state).dataViewById[dataViewId];
  return {
    name: dataView.name,
    defaultName: dataView.defaultName,
  };
};

export const getViewsAffectedByCondition = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) => {
  return getViewIdsAffectedByCondition(state, conditionId).map((viewId) => getViewById(state, viewId));
};

export const getViewsThatOverrideConditionLocally = (state: RootStateOr<WorkbookState>, conditionId: ConditionId) => {
  const condition = getConditionById(state, conditionId);
  assert(condition, `No condition with ID=${conditionId} present in the state.`);

  // Spatial conditions actually can't be overridden locally, however they are still present at the
  // view level because they can apply to a different attribute for each view.
  if (condition.type === 'PolygonCondition') {
    return [];
  }

  return getViewIdsAffectedByCondition(state, conditionId, true).map((viewId) => getViewById(state, viewId));
};

export const getObjecttypesByDataViewId = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  const uniqueObjecttypes = new Set<Objecttype>();
  const attributeIds = Object.values(getDataViewById(state, dataViewId).attributes).flat();
  for (const attributeId of attributeIds) {
    const objecttype = getObjecttypeById(state, getAttributeById(state, attributeId).objecttypeId);
    if (objecttype) {
      uniqueObjecttypes.add(objecttype);
    }
  }
  return Array.from(uniqueObjecttypes);
};

export const getObjecttypesByViewId = createSelector(
  (state: RootStateOr<WorkbookState>) => state,
  getDataViewIdsByViewId,
  (state, dataViewIds): Objecttype[] => {
    return Array.from(
      dataViewIds.reduce((uniqueObjecttypes, dataViewId) => {
        getObjecttypesByDataViewId(state, dataViewId).forEach((ot) => uniqueObjecttypes.add(ot));
        return uniqueObjecttypes;
      }, new Set<Objecttype>()),
    );
  },
);

export const getConditionsByViewId = createSelector(
  getActiveWorksheetId,
  getConditionsById,
  (state: RootStateOr<WorkbookState>) => slice(state).viewById,
  (state: RootStateOr<WorkbookState>) => slice(state).affectedViewsByConditionId,
  (activeWorksheetId, conditionsById, viewById, affectedViewsByConditionId) => {
    const conditionsByViewId: Record<ViewId, ConditionDto[]> = {};

    Object.values(conditionsById).forEach((condition) => {
      const viewsAffectedByCondition =
        affectedViewsByConditionId[condition.id]?.affectedViews?.[activeWorksheetId]?.map(
          (viewId) => viewById[viewId],
        ) || [];

      viewsAffectedByCondition.forEach((view) => {
        if (!conditionsByViewId[view.id]) {
          conditionsByViewId[view.id] = [];
        }
        if (!conditionsByViewId[view.id].includes(condition)) {
          conditionsByViewId[view.id].push(condition);
        }
      });
    });

    return conditionsByViewId;
  },
);

export const hasConditionsWithMissingMandatoryValue = createSelector(getConditionsById, (conditionsById) => {
  for (const condition of Object.values(conditionsById)) {
    if (condition.settings.mandatory && !conditionHasValues(condition)) {
      return true;
    }
  }
  return false;
});

const getSpatialConditionsForAllViews = createSelector(getConditionsByViewId, (conditionsByViewId) => {
  const spatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
  entries(conditionsByViewId).forEach(([viewId, conditions]) => {
    const spatialConditions = conditions.filter((condition) => condition.type === 'PolygonCondition');
    if (spatialConditions.length) {
      spatialConditionsByViewId[viewId] = spatialConditions;
    }
  });
  return spatialConditionsByViewId;
});

const getEffectiveSpatialConditionsForAllViews = createSelector(
  getSpatialConditionsForAllViews,
  (spatialConditionsByViewId) => {
    const effectiveSpatialConditionsByViewId: Record<ViewId, ConditionDto[]> = {};
    entries(spatialConditionsByViewId).forEach(([viewId, conditions]) => {
      const effectiveSpatialConditions = conditions.filter(isSpatialConditionEffective);
      if (effectiveSpatialConditions.length) {
        effectiveSpatialConditionsByViewId[viewId] = effectiveSpatialConditions;
      }
    });

    return effectiveSpatialConditionsByViewId;
  },
);

function isSpatialConditionEffective(spatialCondition: ConditionDto) {
  // it should have a value with at least one polygon
  return conditionHasValues(spatialCondition);
}

const getSpatialConditionsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getSpatialConditionsForAllViews(state)[viewId] ?? [];

export const getEffectiveSpatialConditionsForView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getEffectiveSpatialConditionsForAllViews(state)[viewId] ?? [];

const getOrderedConditions = (state: RootStateOr<WorkbookState>) =>
  getConditionsAndGroups(state)
    .flatMap((conditionOrGroupId) => {
      const conditionGroup = getConditionGroupById(state, conditionOrGroupId as ConditionGroupId);
      return conditionGroup ? conditionGroup.conditions : (conditionOrGroupId as ConditionId);
    })
    .map((conditionId) => getConditionById(state, conditionId));

export const getAllSpatialOverlapsConditions = (state: RootStateOr<WorkbookState>) =>
  getOrderedConditions(state)
    .filter((condition) => condition.type === 'PolygonCondition')
    .filter(
      (condition) =>
        getSetting(condition, ConditionSettingsKey.toPersistenceKey('SPATIAL_CONDITION_RELATION')) === 'OVERLAPS',
    );

export const viewHasEffectiveSpatialFilter = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getSpatialConditionsForView(state, viewId).length > 0;

export const isSpatialConditionGeometryVisibleInView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  !isOverviewMap(state, viewId) &&
  getSetting(
    getViewById(state, viewId),
    MapViewSettingsKey.toPersistenceKey('DISPLAY_SPATIAL_FILTER_GEOMETRIES'),
    false,
  );

export const getActiveViewId = (state: RootStateOr<WorkbookState>) => slice(state).activeViewId;
export const isActiveView = (state: RootStateOr<WorkbookState>, viewId: ViewId) => getActiveViewId(state) === viewId;

export const isViewInvalid = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  return (
    getInvalidViewsOrDataViews(state).includes(viewId) ||
    getViewById(state, viewId).dataViews.some((dataViewId) => invalidViewsOrDataViews.includes(dataViewId))
  );
};

export const isViewAffectedBySelection = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);
  return getViewById(state, viewId).dataViews.some((dataViewId) =>
    dataViewsAffectedBySelectionChange.includes(dataViewId),
  );
};

export const isViewInvalidOrAffectedBySelection = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const invalidViewsOrDataViews = getInvalidViewsOrDataViews(state);
  if (invalidViewsOrDataViews.includes(viewId)) {
    return true;
  }
  const dataViewsAffectedBySelectionChange = getDataViewsAffectedBySelectionChange(state);
  return getViewById(state, viewId).dataViews.some(
    (dataViewId) =>
      invalidViewsOrDataViews.includes(dataViewId) || dataViewsAffectedBySelectionChange.includes(dataViewId),
  );
};

export const getInvalidViewsOrDataViews = (state: RootStateOr<WorkbookState>) => slice(state).invalidViewsOrDataViews;
const getInvalidViewsOrDataViewsOfActiveWorksheet = (state: RootStateOr<WorkbookState>) => {
  const viewIds = getActiveWorksheetViewIds(state);
  const dataViewIds = viewIds.flatMap((viewId) => getDataViewIdsByViewId(state, viewId));
  return slice(state).invalidViewsOrDataViews.filter(
    (viewIdOrDataViewId) =>
      viewIds.includes(viewIdOrDataViewId as ViewId) || dataViewIds.includes(viewIdOrDataViewId as DataViewId),
  );
};
export const isActiveWorksheetDirty = (state: RootStateOr<WorkbookState>) =>
  getInvalidViewsOrDataViewsOfActiveWorksheet(state).length > 0;
export const getDataViewsAffectedBySelectionChange = (state: RootStateOr<WorkbookState>) =>
  slice(state).dataViewsAffectedBySelectionChange;

export const getMaximizedViewId = (state: RootStateOr<WorkbookState>) => slice(state).maximizedViewId;
export const isMaximizedView = (state: RootStateOr<WorkbookState>, viewId: ViewId) =>
  getMaximizedViewId(state) === viewId;
export const hasForceMaximizedView = (state: RootStateOr<WorkbookState>) =>
  slice(state).forceMaximizePreviousState != null;
export const getSelection = (state: RootStateOr<WorkbookState>) => slice(state).selection;

export const getDataViewIdsWithAllowedSelection = (state: RootStateOr<WorkbookState>) =>
  (Object.keys(slice(state).dataViewById) as DataViewId[]).filter((dataViewId) =>
    getSettingOrSettingDefault(
      getDataViewById(state, dataViewId),
      CommonDataViewSettingsKey.toPersistenceKey('SELECTION_ENABLED'),
    ),
  );

export const isViewOnDemand = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  const isAnyDataViewOnDemand = view.dataViews.some((dataViewId) => {
    const dataView = getDataViewById(state, dataViewId);
    return isDataViewOnDemand(dataView);
  });
  return isAnyDataViewOnDemand && shouldOnDemandBeConsidered(state, slice(state).lastCommand);
};

export function getViewSettingOrSettingDefault<T>(
  state: RootStateOr<WorkbookState>,
  viewId: ViewId,
  persistenceKey: string,
) {
  const viewDto = getViewById(state, viewId);
  return getSettingOrSettingDefault<T>(viewDto, persistenceKey);
}

export function setViewSetting(viewId: ViewId, persistenceKey: string, value: Primitive) {
  return getStore().dispatch(
    executeCommand(Command.CHANGE_VIEW_SETTINGS({ viewId, settings: { [persistenceKey]: value } })),
  );
}

export const isFeatureInfoEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName),
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_ENABLED'),
      'true',
    ) === 'true'
  );
};

// returns setting(s) (without default value) for a given key(s)
export const getDataViewSettings = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  keys: string | string[],
): Record<string, unknown> => {
  const settings = getDataViewById(state, dataViewId).settings;
  const result: Record<string, unknown> = {};
  (Array.isArray(keys) ? keys : [keys]).forEach((key) => {
    result[key] = getSetting(settings, key);
  });
  return result;
};

// returns setting value (without default value) for a given key
export function getDataViewSetting<T>(state: RootStateOr<WorkbookState>, dataViewId: DataViewId, key: string): T {
  return getDataViewSettings(state, dataViewId, key)[key] as T;
}

export function setDataViewSetting(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  persistenceKey: string,
  value: Primitive,
) {
  const viewId = getViewByDataViewId(state, dataViewId).id;
  return getStore().dispatch(
    executeCommand(
      Command.CHANGE_DATA_VIEW_SETTINGS({
        viewId,
        dataViewId,
        settings: { [persistenceKey]: value },
      }),
    ),
  );
}

export function setDataViewSettings(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  newSettings: Record<string, Primitive>,
) {
  const dataViewDto = getDataViewById(state, dataViewId);
  const viewId = getViewByDataViewId(state, dataViewId).id;
  const settings = { ...dataViewDto.settings };
  Object.assign(settings, newSettings);
  return getStore().dispatch(executeCommand(Command.CHANGE_DATA_VIEW_SETTINGS({ viewId, dataViewId, settings })));
}

// returns setting value or default setting value for a given key
export function getDataViewSettingOrSettingDefault<T>(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  persistenceKey: string,
): T {
  return getSettingOrSettingDefault<T>(getDataViewById(state, dataViewId), persistenceKey);
}

// returns default setting value for a given key
export function getDataViewDefaultSetting<T>(
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  key: string,
): T {
  return getDefaultSetting<T>(getDataViewById(state, dataViewId), key);
}

export const isFeatureInfoAutoModeEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName),
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_AUTO_MODE_ENABLED'),
      'true',
    ) === 'true'
  );
};

export const getObjectInfoHeadlineType = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
): FeatureInfoHeadlineType => {
  if (isFeatureInfoAutoModeEnabled(state, dataViewId, bundleName)) {
    return 'auto';
  }
  return getSetting(
    getAttributeBundleSettings(state, dataViewId, bundleName),
    AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_TYPE'),
    'auto',
  );
};

export const getObjectInfoCustomHeadline = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return getSetting<string>(
    getAttributeBundleSettings(state, dataViewId, bundleName),
    AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_HEADLINE_CUSTOM'),
  );
};

export const getIsPieChartSliceValueResolvingEnabled = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return (
    getSetting(
      getAttributeBundleSettings(state, dataViewId, bundleName),
      AttributeBundleSettingsKey.toPersistenceKey('FEATURE_INFO_PIE_CHART_SLICE_RESOLVING_ENABLED'),
      'true',
    ) === 'true'
  );
};

export const getObjectInfoAttributeBundleName = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  objecttypeId: ObjecttypeIdString,
) => {
  return (getDataViewById(state, dataViewId).objecttypeIdToAttributeBundleName ?? {})[objecttypeId];
};

export const selectAttributeBundleSettings = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getDataViewById(state, dataViewId)?.attributeBundleSettings;
};

export const dataViewPointSymbolization = (state: RootStateOr<WorkbookState>, dataViewId: DataViewId) => {
  return getSetting(getDataViewById(state, dataViewId), MapDataViewSettingsKey.toPersistenceKey('POINT_SYMBOLIZATION'));
};

export const getAttributeBundleSettings = (
  state: RootStateOr<WorkbookState>,
  dataViewId: DataViewId,
  bundleName: AttributeBundleName,
) => {
  return getDataViewById(state, dataViewId)?.attributeBundleSettings[bundleName];
};

/**
 * Clone view to a different worksheet
 *
 * Note: This is just a helper function. Since it's not a reducer it mustn't modify the state. Like
 * regular code, it calls selectors and dispatches a thunk.
 */
export const copyViewToWorksheet = (store: Store, viewId: ViewId, targetWorksheetId: WorksheetId) => {
  const copyViewPayload: CopyViewCommandPayload = {
    viewId,
    targetWorksheetId,
  };
  const view = getViewById(getStore().getState(), viewId);
  if (view.type === ViewType.MAP) {
    const currentMapExtent = loadExtentFromSession(getWorkbookNameAndHash(getStore().getState()).id, viewId);
    if (currentMapExtent) {
      const changeCurrentExtentPayload: ChangeCurrentExtentCommandPayload = {
        viewId,
        currentExtent: currentMapExtent,
      };

      const copyViewCommand = Command.COPY_VIEW(copyViewPayload);
      const changeCurrentExtentCommand = Command.CHANGE_CURRENT_EXTENT(changeCurrentExtentPayload);
      const composite = Command.COMPOSITE_COMMAND({
        commands: [changeCurrentExtentCommand, copyViewCommand],
      });
      return getStore().dispatch(executeCommand(composite)).unwrap();
    }
  }
  return getStore()
    .dispatch(executeCommand(Command.COPY_VIEW(copyViewPayload)))
    .unwrap();
};

/**
 * Create a new worksheet
 */
export const addWorksheet = (store: Store, worksheetName: string) => {
  return getStore()
    .dispatch(executeCommand(Command.ADD_WORKSHEET({ worksheetName })))
    .unwrap()
    .then(() => {
      const worksheets = getWorksheets(getStore().getState());
      // last one is the new worksheet
      return worksheets[worksheets.length - 1].id;
    });
};

/**
 * Creates duplicate of given worksheet
 *
 * @param store - Redux store
 * @param worksheetName - name of the new worksheet to be created
 * @param sourceWorksheetId - id of the
 *   source worksheet
 * @return executeCommand thunk
 */
export const duplicateWorksheet = (store: Store, worksheetName: string, sourceWorksheetId: WorksheetId) => {
  const copyWorksheetCommand = Command.COPY_WORKSHEET({ worksheetName, sourceWorksheetId });
  return getStore()
    .dispatch(executeCommand(copyWorksheetCommand))
    .unwrap()
    .then(() => {
      const worksheets = getWorksheets(getStore().getState());
      // duplicated worksheet is always placed after the original
      const sourceWorksheetIndex = worksheets.findIndex(({ id }) => id === sourceWorksheetId);
      return worksheets[sourceWorksheetIndex + 1].id;
    });
};

export const setWorkbookSettings = (store: Store, settings: Settings) => {
  return getStore()
    .dispatch(executeCommand(Command.CHANGE_WORKBOOK_SETTINGS({ settings })))
    .unwrap();
};

/**
 * Save current extent for all map views in the workbook.
 *
 * @param store - Redux store
 * @return Promise of the new workbook state or void, if there was no map views
 */
export const saveMapViewsExtent = (store: Store) => {
  const workbookNameAndHash = getWorkbookNameAndHash(getStore().getState());
  const mapViews = getViews(getStore().getState()).filter((view) => view.type === ViewType.MAP);
  const commands = mapViews.flatMap(({ id: viewId }) => {
    const mapExtent = loadExtentFromSession(workbookNameAndHash.id, viewId);
    return mapExtent ?
        [
          Command.CHANGE_CURRENT_EXTENT({
            viewId,
            currentExtent: mapExtent,
          }),
        ]
      : [];
  });
  if (commands.length === 0) {
    return Promise.resolve();
  }
  return getStore()
    .dispatch(executeCommand(Command.COMPOSITE_COMMAND({ commands })))
    .unwrap();
};

export const isWorksheetOnDemandOverlayVisible = (state: RootStateOr<WorkbookState>) =>
  isWorksheetOnDemand(getActiveWorksheet(state)) && isActiveWorksheetDirty(state);

const shouldOnDemandBeConsidered = (state: RootStateOr<WorkbookState>, command: Command | undefined) => {
  return (
    !isReportMode(state) &&
    !isCommandOfType(command, [
      Command.SET_SELECTION.type,
      Command.INVERT_SELECTION.type,
      Command.ADD_VIEW.type,
      Command.ADD_NEW_VIEW_WITH_DATA_VIEW.type,
    ]) &&
    !isCommandOfTypeInViewType(state, command, ViewType.IMAGE, Command.CHANGE_VIEW_SETTINGS.type) &&
    !isCommandOfTypeInViewType(state, command, ViewType.TEXT, Command.CHANGE_VIEW_SETTINGS.type) &&
    !isHierarchyLevelChangeCommandDueToScaleChange(command) &&
    !isSavingMapExtentCompositeCommand(state, command)
  );
};

function isCommandOfType(command: Command | undefined, commandTypes: string[]) {
  return command && commandTypes.includes(command.type);
}

function isCommandOfTypeInViewType(
  state: RootStateOr<WorkbookState>,
  command: Command | undefined,
  viewType: ViewType,
  cmdType: string,
) {
  const viewId = command && (command.payload as { viewId: ViewId })?.viewId;
  if (!viewId) {
    return;
  }
  const view = getViewById(state, viewId);
  return view && view.type === viewType && command.type === cmdType;
}

function isHierarchyLevelChangeCommandDueToScaleChange(command?: Command) {
  return (
    command &&
    command.type === Command.SET_DATA_VIEW_ATTRIBUTE_MODIFICATION.type &&
    (command.payload as SetDataViewAttributeModificationCommandPayload).modificationType ===
      AttributeModificationKey.LOCAL_HIERARCHY_LEVEL
  );
}

export const getCommandErrorKey = (state: RootStateOr<WorkbookState>) => slice(state).commandErrorKey;
export const getCommandErrorDetails = (state: RootStateOr<WorkbookState>) => slice(state).commandErrorDetail;

function isSavingMapExtentCompositeCommand(state: RootStateOr<WorkbookState>, possibleCompositeCommand?: Command) {
  if (possibleCompositeCommand && possibleCompositeCommand.type === Command.COMPOSITE_COMMAND.type) {
    const simpleCommands = (possibleCompositeCommand.payload as CompositeCommandPayload).commands;
    return simpleCommands.length === 1 && isSavingMapExtentCommand(state, simpleCommands[0]);
  }
  return false;
}

function isSavingMapExtentCommand(state: RootStateOr<WorkbookState>, command: Command) {
  return isCommandOfTypeInViewType(state, command, ViewType.MAP, Command.CHANGE_CURRENT_EXTENT.type);
}

export function isReportMode(state: RootStateOr<WorkbookState>) {
  return slice(state).reportMode;
}

export function isDataViewOnDemand(dataView: DataViewDto) {
  return (
    getSetting<QueryExecutionStrategyType>(
      dataView,
      CommonDataViewSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY'),
    ) === 'onDemand'
  );
}

export function isWorksheetOnDemand(worksheet: WorksheetDto) {
  return (
    getSetting<QueryExecutionStrategyType>(
      worksheet,
      WorksheetSettingsKey.toPersistenceKey('QUERY_EXECUTION_STRATEGY'),
    ) === 'onDemand'
  );
}

function isActiveWorksheetSettingOnDemand(state: RootStateOr<WorkbookState>) {
  const activeWorksheet = getActiveWorksheet(state);
  return activeWorksheet && isWorksheetOnDemand(activeWorksheet);
}

export const getOnDemandLayersIds = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(
        (layer) =>
          layer.settings.dataViewId &&
          isDataViewOnDemand(getDataViewById(state, layer.settings.dataViewId as DataViewId)),
      )
      .map((layer) => layer.id);
  }
  return [];
};

export const getAutomaticallyUpdatingLayersIds = (state: RootStateOr<WorkbookState>, viewId: ViewId) => {
  const view = getViewById(state, viewId);
  if (view.type === ViewType.MAP) {
    return Object.values(view.layerDefinitions)
      .filter(
        (layer) =>
          layer.settings.dataViewId == null ||
          !isDataViewOnDemand(getDataViewById(state, layer.settings.dataViewId as DataViewId)),
      )
      .map((layer) => layer.id);
  }
  return [];
};

export const layerById = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  const view = getViewById(state, viewId)!;
  return view.layerDefinitions[layerId];
};

export function getLayerName(state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) {
  return (
    getSetting<string>(layerById(state, viewId, layerId), LayerSettingsKey.toPersistenceKey('LAYER_NAME')) ??
    getDataViewById(state, layerId as DataViewId).name
  );
}

export const getLayerDataByLayerId = (state: RootStateOr<WorkbookState>, layerId: LayerId) => {
  return getLayerData(state, getViewByLayerId(state, layerId).id, layerId);
};

export const getLayerData = (state: RootStateOr<WorkbookState>, viewId: ViewId, layerId: LayerId) => {
  return {
    id: layerId,
    definition: layerById(state, viewId, layerId),
    dataView: getDataViewById(state, toDataViewId(layerId)),
  };
};

export const getLastCommand = (state: RootStateOr<WorkbookState>) => {
  return slice(state).lastCommand;
};

function createCommandErrorDetails(serverErrorPayload?: Problem) {
  if (serverErrorPayload?.key === WORKBOOK_ERROR_KEYS.NO_SPACE_FOR_VIEW_IN_WORKSHEET_GRID) {
    return { worksheetId: serverErrorPayload.detail };
  }
}

function updateDataViewInvalidationState(
  state: WorkbookState,
  invalidViewsOrDataViews: (ViewId | DataViewId)[],
  invalidLayerIdsByViewId: Record<ViewId, LayerId[]>,
  dataViewsAffectedBySelectionChange: DataViewId[],
) {
  state.invalidViewsOrDataViews = invalidViewsOrDataViews;
  state.invalidLayerIdsByViewId = invalidLayerIdsByViewId;
  state.dataViewsAffectedBySelectionChange = dataViewsAffectedBySelectionChange;

  if (
    !getActiveView(state) || // If view was deleted
    (isActiveWorksheetDirty(state) && isActiveWorksheetOnDemand(state))
  ) {
    activateViewInternally(state); // Deactivate active view
  }
}

function activateViewInternally(state: WorkbookState, viewId?: ViewId) {
  if (viewId && isWorksheetOnDemandOverlayVisible(state)) {
    return; // don't activate a view when the worksheet on demand overlay is visible
  }
  state.activeViewId = viewId;
}

function updateActiveLayerId(
  state: WorkbookState,
  previousActiveViewState: ViewWithMetadata | undefined,
  command: Command,
) {
  const activeView = getActiveView(state);
  if (activeView && previousActiveViewState) {
    const viewChangedToMap = activeView.type === ViewType.MAP && previousActiveViewState.type !== ViewType.MAP;
    if (isCommandType(command, Command.ADD_LAYER_GROUP)) {
      const layerIdsBefore = new Set(getViewLayerIds(previousActiveViewState));
      const layerIdsAfter = getViewLayerIds(activeView);
      state.activeLayerId = layerIdsAfter.find((newId) => !layerIdsBefore.has(newId));
    } else if (viewChangedToMap && isCommandType(command, Command.CHANGE_VISUALIZATION)) {
      state.activeLayerId = command.payload.dataViewId;
    } else if (viewChangedToMap && isCommandType(command, Command.ADD_ATTRIBUTE)) {
      state.activeLayerId = activeView.dataViews.at(-1);
    }
  }
  const activeLayerId = state.activeLayerId;
  if (activeLayerId && !Object.values(state.viewById).some((view) => view.layerDefinitions?.[activeLayerId] != null)) {
    delete state.activeLayerId;
  }
}

function unsetMaximizedViewIfViewDoesNotExist(state: WorkbookState, command: Command) {
  if (isCommandType(command, Command.REMOVE_VIEW)) {
    const maximizedViewId = getMaximizedViewId(state);
    if (maximizedViewId && getViewById(state, maximizedViewId) == null) {
      state.maximizedViewId = '';
    }
  }
}

function getViewLayerIds(view: ViewWithMetadata) {
  return Object.keys(view.layerDefinitions ?? {}) as LayerId[];
}

function getInitialMaximizedViewIdFromUrlParam(parameterName: string) {
  return (getUrlParam({ name: parameterName }) as ViewId) || '';
}

export function canManageObjecttype(objecttypeId: ObjecttypeIdString) {
  const objecttype = getObjecttypeById(getStore().getState(), objecttypeId);
  return (
    objecttype != null &&
    isFeatureAvailable('ADMINISTRATION') &&
    isWorkbookVisible(getStore().getState()) &&
    hasPrivilege(objecttype.currentUserPrivileges, 'visible') &&
    !isObjecttypeBound(objecttype)
  );
}

export const canCreateMapContext = createSelector(
  [getActiveOrFirstMapViewId, isAnyDefaultMapDefined],
  (viewId, anyDefaultMapDefined) => viewId !== undefined || anyDefaultMapDefined,
);

// Client-side state persistence

getStore().subscribe(() => {
  const state = slice(getStore().getState());
  persistPanelState(state);
  // state.workbookNameAndHash is always here on an open workbook, but the '?' is needed for some tests
  // that trigger this without initializing the workbook store first
  cadenzaSessionStorage.setItem(WORKBOOK_CHANGED_KEY, state.clientChanged ? getWorkbookName(state) : '');
});

observeStore(getStore(), getWorkbookNameAndHash, updateWorkbookUrl);

function updateWorkbookUrl({ hash: workbookHash }: WorkbookNameAndHash) {
  if (workbookHash) {
    const url = new URL(getUrlWithSlugs() ?? location.href);
    url.searchParams.set('workbookHash', workbookHash);
    history.pushState(null, '', url.toString());
  }
}

function getUrlWithSlugs() {
  const state = getStore().getState();
  const worksheet = getActiveWorksheet(state);
  if (worksheet) {
    const workbookName = getWorkbookName(state);
    const workbookPrintName = getWorkbookPrintName(state);
    const { id: worksheetId, printName: worksheetPrintName } = worksheet;
    const contextPath = window.Disy.contextPath;
    const path = location.pathname.substring(contextPath.length);
    const slugifiedPath = path
      .replace(/\/workbooks\/[^/]+/, `/workbooks/${slugify(workbookPrintName)},${workbookName}`)
      .replace(/\/worksheets\/([^/?]+)/, `/worksheets/${slugify(worksheetPrintName)},${worksheetId}`);
    return location.href.replace(location.pathname, contextPath + slugifiedPath);
  }
}

export function reorderLayers(viewId: ViewId, newLayerOrder: LayerId[], parentLayerId?: LayerId) {
  return getStore().dispatch(
    executeCommand(
      Command.REORDER_LAYERS({
        viewId,
        newLayerOrder,
        parentLayerId,
      }),
    ),
  );
}
